import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators
} from '@angular/forms';
import { ComponentRendering, Field } from '@sitecore-jss/sitecore-jss-angular';

import {
  FormFieldsGeneratorService,
  FormFields
} from './../../../shared/services/form-fields-generator.service';

import { LoginRequest, LoginService } from './../../services/login.service';
import { AB_DASHBOARD_URL } from './../../../shared/constants';
import { GetItemListFieldsService } from './../../../shared/services/get-item-list-fields.service';
import { Item } from '@sitecore-jss/sitecore-jss/types/layout';
import { NavigationStart, Router, RouterEvent } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { InitialViewService } from './../../../shared/services/initial-view.service';
import { IdleService } from './../../../session/services/idle.service';
import { AccountDetailsService } from './../../../shared/services/account-details.service';
import { UserAgentService } from './../../../shared/services/user-agent.service';
import { ConfigurationSettingsContextService } from '../../../shared/services/configuration-settings-context.service';

type LoginMethod = 'qr' | 'app' | 'token';
@Component({
  selector: 'ab-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() rendering: ComponentRendering;
  @ViewChild('usernameInput') usernameInput: ElementRef;
  @ViewChild('passwordInput') passwordInput: ElementRef;

  public title: Field;
  public submitButtonText: Field;
  public items: { [key: string]: Item };
  public formFields: FormFields;
  public apiError = false;
  public userBlockedError = false;
  public invalidCredentialsError = false;
  public invalidLoginMessage = '';
  public isLoading = false;
  public loginForm = new UntypedFormGroup({
    username: new UntypedFormControl('', Validators.required),
    logincode: new UntypedFormControl('', Validators.required),
    otpType: new UntypedFormControl('')
  });
  public routerSubscription: Subscription;
  public isDesktop: boolean;
  public loginMethod: LoginMethod;
  public loginWith: { [key: string]: { [name: string]: Field } };
  public helpSteps: Item;
  public pageTitle: Field;
  public toggleButton: Field;
  public description: Field;
  public qrCodeImage: string;
  public loginRequestId: string;
  public displaySentooLoginScreen: boolean;

  constructor(
    protected router: Router,
    private formFieldsGeneratorService: FormFieldsGeneratorService,
    private loginService: LoginService,
    private getItemListFieldsService: GetItemListFieldsService,
    private accountDetailsService: AccountDetailsService,
    private initialViewService: InitialViewService,
    private idleService: IdleService,
    private userAgentService: UserAgentService,
    private configurationSettingsContextService: ConfigurationSettingsContextService,
    private renderer: Renderer2
  ) {}

  get username() {
    return this.loginForm.get('username');
  }

  get logincode() {
    return this.loginForm.get('logincode');
  }

  get otpType() {
    return this.loginForm.get('otpType');
  }

  get allowQRLogin() {
    return this.configurationSettingsContextService?.getConfigurationSettingsValidationAttributeValue(
      'AllowQRLogin'
    );
  }

  ngOnInit() {
    this.idleService.stopRefreshToken();
    this.submitButtonText = this.rendering?.fields?.submitButtonText as Field;
    this.isDesktop = this.userAgentService.isDesktopDevice();
    this.loginMethod = 'app';
    this.displaySentooLoginScreen = this.allowQRLogin;
    this.formFields = this.formFieldsGeneratorService.generateFormFields(
      this.rendering
    ) as FormFields;
    if (
      this.rendering?.fields?.items &&
      (<Item[]>this.rendering?.fields?.items).length !== 0
    ) {
      this.items = this.getItemListFieldsService.getItems(
        this.rendering.fields.items as Item[]
      );
      this.loginWith = this.getItemListFieldsService.getFields(
        (this.rendering.fields.items as Item[]).filter(
          (o) => o.name === 'LoginWith'
        )[0]
      );
    }

    if (this.displaySentooLoginScreen) {
      this.renderer.setStyle(document.body, 'background-color', '#0e4c8f');
    }
  }

  ngAfterViewInit() {
    if (this.usernameInput) {
      this.usernameInput.nativeElement.focus();
    }
  }

  loginWithToken() {
    this.loginMethod = 'token';
  }

  loginWithApp() {
    this.loginMethod = 'app';
  }

  submitAction() {
    if (this.loginForm.valid) {
      this.loginService.login(this.loginForm.value as LoginRequest).subscribe(
        (response) => {
          this.handleResponse(response);
        },
        (error) => {
          this.handleResponse(error);
        }
      );
    }
  }

  changeToken(tokenValue) {
    if (tokenValue) {
      this.otpType?.setValue(tokenValue);
      this.username?.setValue(this.usernameInput.nativeElement.value);
      setTimeout(() => {
        this.passwordInput.nativeElement.focus();
      });
    }
  }

  handleResponse(response) {
    if (!response || !response.callIsSuccess) {
      this.apiError = true;
      this.loginForm.reset({
        otpType: ''
      });
      if (response.error) {
        this.userBlockedError = response.error.errorCode === 'User.Blocked';
        this.invalidCredentialsError =
          response.error.errorCode === 'User.Failed';
        this.invalidLoginMessage = response.error.loginErrorMessage;
      }
    } else {
      this.idleService.doRefreshToken();
      this.accountDetailsService.setFromLogin();

      this.isLoading = true;

      this.routerSubscription = this.router.events
        .pipe(filter((event: RouterEvent) => event instanceof NavigationStart))
        .subscribe(() => {
          this.initialViewService.getInitialView().subscribe(() => {});
        });
      this.router.navigate([AB_DASHBOARD_URL]);
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
